import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, PageSizeType, Sort, Text } from 'design-system-web';
import { useColumnsHeaders } from './hooks/useColumnHeaders';
import { useContentWaitlistActions } from 'lane-shared/hooks/contentState';

import { RowData, waitlistItemToRowDataMapper, getSort } from './helpers';
import { useGetQuantityWaitlist } from './hooks/useGetQuantityWaitlist';

import styles from './styles.scss';
import {
  formatISODateToHourMinutes,
  formatISODateToMonthDayYear,
} from './helpers/formatters';
import { DateTime } from 'luxon';

function getFormattedEventDateTime(date: any, timeZone: string): string {
  return `${formatISODateToMonthDayYear(
    date,
    timeZone
  )} - ${formatISODateToHourMinutes(date, timeZone)}`;
}

const TRANSLATION_KEYS = {
  emptyMessage: 'web.admin.channel.waitlistManagement.table.emptyMessage',
  serverError: 'web.admin.channel.waitlistManagement.table.serverError',
  leaveWaitlistSuccess:
    'web.admin.channel.waitlistManagement.table.remove.success',
  remove: 'web.admin.channel.waitlistManagement.table.remove',
};

type Props<T> = {
  channelId: string;
  contentId: string;
  timeZone: string;
  content: T;
};

const DEFAULT_PAGE_SIZE = 25;

export function WaitlistTableView<
  T extends { startDate: string; endDate: string } = any
>({ channelId, contentId, timeZone, content }: Props<T>) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<PageSizeType>(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<Sort>({});
  const [error, setError] = useState<string | null>(null);

  const { leaveWaitlist } = useContentWaitlistActions();

  const { t } = useTranslation();

  const handleRemove = async (id: string) => {
    try {
      await leaveWaitlist(id, contentId);
      await getQuantityWaitlists({
        contentId,
        channelId,
        search: {
          sortBy: getSort(sort),
          afterEndDate: DateTime.now().setZone(timeZone).toISO(),
        },
        page,
        pageSize,
      });

      window.Toast.show(t(TRANSLATION_KEYS.leaveWaitlistSuccess));
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (ex) {
      window.Toast.show(t(TRANSLATION_KEYS.serverError));
    }
  };

  const { columns } = useColumnsHeaders(timeZone);

  const {
    getQuantityWaitlists,
    isLoading,
    waitlists,
  } = useGetQuantityWaitlist();

  useEffect(() => {
    const fetchQuantityWaitlists = async () => {
      try {
        await getQuantityWaitlists({
          contentId,
          channelId,
          search: {
            sortBy: getSort(sort),
            afterEndDate: DateTime.now().setZone(timeZone).toISO(),
          },
          page,
          pageSize,
        });
        setError(null);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchQuantityWaitlists();
  }, [contentId, channelId, page, pageSize, sort]);

  const totalRows = waitlists?.pageInfo?.total || 0;
  const waitlistItems = waitlists?.items || [];
  const rowActions = [
    {
      label: t(TRANSLATION_KEYS.remove),
      onClick: (rowinfo: any) => {
        handleRemove(rowinfo.id);
      },
    },
  ];

  if (error) {
    window.Toast.show(<Text>{t(TRANSLATION_KEYS.serverError)}</Text>);
  }

  const isEvent = Boolean(content?.startDate && content?.endDate);

  return (
    <>
      {isEvent ? (
        <div className={styles.eventDate}>
          {t('web.admin.channel.waitlistManagement.table.eventDateTime', {
            startDateTime: getFormattedEventDateTime(
              content?.startDate,
              timeZone
            ),
            endDateTime: getFormattedEventDateTime(content?.endDate, timeZone),
          })}
        </div>
      ) : null}

      <Table<RowData>
        isLoading={isLoading}
        columns={columns}
        data={waitlistItemToRowDataMapper(timeZone, waitlistItems)}
        pagination="server"
        totalRows={totalRows}
        pageSize={pageSize}
        page={page}
        rowActions={rowActions}
        onPageChange={setPage}
        onSortChange={setSort}
        onPageSizeChange={setPageSize}
        emptyMessage={t(TRANSLATION_KEYS.emptyMessage)}
        tableKey="waitlistManagement.waitlistTableView"
      />
    </>
  );
}
